import React, { useState, useEffect } from 'react';
import { Shield, Activity, AlertTriangle, Settings, TrendingUp, MapPin, Users, Lock, Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { toast } from '../../../components/ui/Toast';
import patternDetectionService from '../../../services/patternDetectionService';
import Icon from '../../../components/AppIcon';


const PatternDetectionDashboard = () => {
  const [config, setConfig] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [recentIncidents, setRecentIncidents] = useState([]);
  const [ipWhitelist, setIpWhitelist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'config', 'whitelist'
  
  // Add IP form
  const [showAddIP, setShowAddIP] = useState(false);
  const [newIP, setNewIP] = useState('');
  const [newIPDescription, setNewIPDescription] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configData, statsData, incidentsData, whitelistData] = await Promise.all([
        patternDetectionService?.getPatternConfig(),
        patternDetectionService?.getPatternStatistics(30),
        patternDetectionService?.getRecentPatternIncidents(10),
        patternDetectionService?.getIPWhitelist()
      ]);

      setConfig(configData);
      setStatistics(statsData);
      setRecentIncidents(incidentsData);
      setIpWhitelist(whitelistData);
    } catch (error) {
      console.error('Error loading pattern detection data:', error);
      toast?.error('Failed to load pattern detection data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await patternDetectionService?.updatePatternConfig(config);
      toast?.success('Pattern detection configuration updated successfully');
    } catch (error) {
      console.error('Error saving config:', error);
      toast?.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAddIP = async () => {
    if (!newIP) {
      toast?.error('Please enter an IP address');
      return;
    }

    try {
      await patternDetectionService?.addIPToWhitelist(newIP, newIPDescription);
      toast?.success('IP address added to whitelist');
      setNewIP('');
      setNewIPDescription('');
      setShowAddIP(false);
      loadData();
    } catch (error) {
      console.error('Error adding IP:', error);
      toast?.error('Failed to add IP to whitelist');
    }
  };

  const handleRemoveIP = async (whitelistId) => {
    try {
      await patternDetectionService?.removeIPFromWhitelist(whitelistId);
      toast?.success('IP address removed from whitelist');
      loadData();
    } catch (error) {
      console.error('Error removing IP:', error);
      toast?.error('Failed to remove IP from whitelist');
    }
  };

  const handleToggleIP = async (whitelistId, isActive) => {
    try {
      await patternDetectionService?.toggleIPWhitelist(whitelistId, !isActive);
      toast?.success(`IP address ${!isActive ? 'enabled' : 'disabled'}`);
      loadData();
    } catch (error) {
      console.error('Error toggling IP:', error);
      toast?.error('Failed to toggle IP status');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'text-blue-600 bg-blue-50',
      medium: 'text-yellow-600 bg-yellow-50',
      high: 'text-orange-600 bg-orange-50',
      critical: 'text-red-600 bg-red-50'
    };
    return colors?.[severity] || colors?.low;
  };

  const getPatternTypeIcon = (type) => {
    const icons = {
      failed_auth: Lock,
      location_anomaly: MapPin,
      bulk_permission: Users
    };
    return icons?.[type] || AlertTriangle;
  };

  const getPatternTypeLabel = (type) => {
    const labels = {
      failed_auth: 'Failed Authentication',
      location_anomaly: 'Location Anomaly',
      bulk_permission: 'Bulk Permission Change'
    };
    return labels?.[type] || type;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              Suspicious Pattern Detection
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Monitor and configure automatic detection of suspicious activities
            </p>
          </div>
          {statistics && (
            <div className="flex gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{statistics?.total_incidents}</div>
                <div className="text-xs text-gray-600">Total Incidents</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{statistics?.auto_escalated}</div>
                <div className="text-xs text-gray-600">Auto-Escalated</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{statistics?.avg_risk_score}</div>
                <div className="text-xs text-gray-600">Avg Risk Score</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex px-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Activity className="w-4 h-4 inline mr-1" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'config' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-1" />
            Configuration
          </button>
          <button
            onClick={() => setActiveTab('whitelist')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'whitelist' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Lock className="w-4 h-4 inline mr-1" />
            IP Whitelist
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <Lock className="w-5 h-5 text-red-600" />
                  <span className="text-2xl font-bold text-red-600">
                    {statistics?.by_pattern_type?.failed_auth || 0}
                  </span>
                </div>
                <h3 className="text-sm font-medium text-gray-900">Failed Auth Attempts</h3>
                <p className="text-xs text-gray-600 mt-1">Detected in last 30 days</p>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <MapPin className="w-5 h-5 text-orange-600" />
                  <span className="text-2xl font-bold text-orange-600">
                    {statistics?.by_pattern_type?.location_anomaly || 0}
                  </span>
                </div>
                <h3 className="text-sm font-medium text-gray-900">Location Anomalies</h3>
                <p className="text-xs text-gray-600 mt-1">Unusual access locations</p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  <span className="text-2xl font-bold text-purple-600">
                    {statistics?.by_pattern_type?.bulk_permission || 0}
                  </span>
                </div>
                <h3 className="text-sm font-medium text-gray-900">Bulk Permission Changes</h3>
                <p className="text-xs text-gray-600 mt-1">Rapid role modifications</p>
              </div>
            </div>

            {/* Recent Incidents */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Recent Pattern Incidents
              </h3>
              <div className="space-y-3">
                {recentIncidents?.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No pattern incidents detected</p>
                  </div>
                ) : (
                  recentIncidents?.map((incident) => {
                    const Icon = getPatternTypeIcon(incident?.pattern_type);
                    return (
                      <div
                        key={incident?.id}
                        className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <Icon className="w-5 h-5 mt-1 text-gray-600" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-gray-900">
                                  {incident?.incident?.title}
                                </h4>
                                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                  getSeverityColor(incident?.incident?.severity)
                                }`}>
                                  {incident?.incident?.severity?.toUpperCase()}
                                </span>
                                <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700">
                                  {getPatternTypeLabel(incident?.pattern_type)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">
                                {incident?.incident?.description}
                              </p>
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span>
                                  Risk Score: <span className="font-semibold text-orange-600">{incident?.risk_score}</span>
                                </span>
                                <span>
                                  {new Date(incident?.detection_timestamp)?.toLocaleString()}
                                </span>
                                {incident?.auto_escalated && (
                                  <span className="text-red-600 font-medium">
                                    ⚠️ Auto-Escalated
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'config' && config && (
          <div className="space-y-6">
            {/* Failed Auth Detection */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">Failed Authentication Detection</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config?.failed_auth_enabled}
                    onChange={(e) => setConfig({ ...config, failed_auth_enabled: e?.target?.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label className="text-sm font-medium text-gray-700">Enable Failed Auth Detection</label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Failed Attempts Threshold
                    </label>
                    <Input
                      type="number"
                      value={config?.failed_auth_threshold}
                      onChange={(e) => setConfig({ ...config, failed_auth_threshold: parseInt(e?.target?.value) })}
                      disabled={!config?.failed_auth_enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time Window (minutes)
                    </label>
                    <Input
                      type="number"
                      value={config?.failed_auth_window_minutes}
                      onChange={(e) => setConfig({ ...config, failed_auth_window_minutes: parseInt(e?.target?.value) })}
                      disabled={!config?.failed_auth_enabled}
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={config?.failed_auth_per_user_enabled}
                      onChange={(e) => setConfig({ ...config, failed_auth_per_user_enabled: e?.target?.checked })}
                      disabled={!config?.failed_auth_enabled}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <label className="text-sm text-gray-700">Track per User</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={config?.failed_auth_per_ip_enabled}
                      onChange={(e) => setConfig({ ...config, failed_auth_per_ip_enabled: e?.target?.checked })}
                      disabled={!config?.failed_auth_enabled}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <label className="text-sm text-gray-700">Track per IP Address</label>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Anomaly Detection */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-orange-600" />
                <h3 className="text-lg font-semibold text-gray-900">Location Anomaly Detection</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config?.location_detection_enabled}
                    onChange={(e) => setConfig({ ...config, location_detection_enabled: e?.target?.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label className="text-sm font-medium text-gray-700">Enable Location Detection</label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Distance Threshold (km)
                    </label>
                    <Input
                      type="number"
                      value={config?.location_distance_threshold_km}
                      onChange={(e) => setConfig({ ...config, location_distance_threshold_km: parseInt(e?.target?.value) })}
                      disabled={!config?.location_detection_enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Risk Score Threshold (0-100)
                    </label>
                    <Input
                      type="number"
                      value={config?.location_risk_threshold}
                      onChange={(e) => setConfig({ ...config, location_risk_threshold: parseInt(e?.target?.value) })}
                      disabled={!config?.location_detection_enabled}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config?.location_new_country_alert}
                    onChange={(e) => setConfig({ ...config, location_new_country_alert: e?.target?.checked })}
                    disabled={!config?.location_detection_enabled}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label className="text-sm text-gray-700">Alert on New Country Login</label>
                </div>
              </div>
            </div>

            {/* Bulk Permission Change Detection */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Bulk Permission Change Detection</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config?.bulk_permission_enabled}
                    onChange={(e) => setConfig({ ...config, bulk_permission_enabled: e?.target?.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label className="text-sm font-medium text-gray-700">Enable Bulk Permission Detection</label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Change Threshold
                    </label>
                    <Input
                      type="number"
                      value={config?.bulk_permission_threshold}
                      onChange={(e) => setConfig({ ...config, bulk_permission_threshold: parseInt(e?.target?.value) })}
                      disabled={!config?.bulk_permission_enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time Window (minutes)
                    </label>
                    <Input
                      type="number"
                      value={config?.bulk_permission_window_minutes}
                      onChange={(e) => setConfig({ ...config, bulk_permission_window_minutes: parseInt(e?.target?.value) })}
                      disabled={!config?.bulk_permission_enabled}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config?.privilege_escalation_detection}
                    onChange={(e) => setConfig({ ...config, privilege_escalation_detection: e?.target?.checked })}
                    disabled={!config?.bulk_permission_enabled}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label className="text-sm text-gray-700">Detect Privilege Escalation Patterns</label>
                </div>
              </div>
            </div>

            {/* Auto-Escalation Configuration */}
            <div className="border rounded-lg p-4 bg-blue-50">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Automatic Escalation</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config?.auto_escalation_enabled}
                    onChange={(e) => setConfig({ ...config, auto_escalation_enabled: e?.target?.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label className="text-sm font-medium text-gray-700">Enable Automatic Escalation</label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Escalation Severity Threshold
                  </label>
                  <Select
                    value={config?.escalation_severity_threshold}
                    onChange={(e) => setConfig({ ...config, escalation_severity_threshold: e?.target?.value })}
                    disabled={!config?.auto_escalation_enabled}
                  >
                    <option value="medium">Medium and Above</option>
                    <option value="high">High and Above</option>
                    <option value="critical">Critical Only</option>
                  </Select>
                </div>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={config?.escalation_sms_enabled}
                      onChange={(e) => setConfig({ ...config, escalation_sms_enabled: e?.target?.checked })}
                      disabled={!config?.auto_escalation_enabled}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <label className="text-sm text-gray-700">Send SMS Alerts</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={config?.escalation_email_enabled}
                      onChange={(e) => setConfig({ ...config, escalation_email_enabled: e?.target?.checked })}
                      disabled={!config?.auto_escalation_enabled}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <label className="text-sm text-gray-700">Send Email Alerts</label>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={handleSaveConfig} disabled={saving}>
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'whitelist' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">IP Address Whitelist</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Whitelisted IPs bypass location anomaly detection
                </p>
              </div>
              <Button onClick={() => setShowAddIP(!showAddIP)}>
                <Plus className="w-4 h-4 mr-1" />
                Add IP
              </Button>
            </div>

            {/* Add IP Form */}
            {showAddIP && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-medium text-gray-900 mb-3">Add IP to Whitelist</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IP Address
                    </label>
                    <Input
                      type="text"
                      placeholder="e.g., 192.168.1.1"
                      value={newIP}
                      onChange={(e) => setNewIP(e?.target?.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <Input
                      type="text"
                      placeholder="e.g., Office network"
                      value={newIPDescription}
                      onChange={(e) => setNewIPDescription(e?.target?.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddIP}>Add IP</Button>
                    <Button variant="secondary" onClick={() => setShowAddIP(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* IP Whitelist Table */}
            <div className="border rounded-lg overflow-hidden">
              {ipWhitelist?.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Lock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No whitelisted IP addresses</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IP Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Added
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {ipWhitelist?.map((ip) => (
                      <tr key={ip?.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {ip?.ip_address}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {ip?.description || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            ip?.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {ip?.is_active ? 'Active' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(ip?.created_at)?.toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleToggleIP(ip?.id, ip?.is_active)}
                              className="text-blue-600 hover:text-blue-900"
                              title={ip?.is_active ? 'Disable' : 'Enable'}
                            >
                              {ip?.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleRemoveIP(ip?.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Remove"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatternDetectionDashboard;